import type {
  UnitInfoInterface,
  UnitInfoScreenBase,
  UnitInterface,
} from 'store/units/types';
import { maxNbrOfDecks, minNbrOfDecks } from 'unitData/constants';
import { UnitTypes } from 'unitData/types';
import { getPortsForUnit } from 'store/units/unitRegistry';

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * Dangerously tries to get separation size from unknown
 * screen deck. Returns fallback as separation size if
 * it fails.
 */
export const tryGetSepSize = (
  unitInfo: UnitInfoInterface,
  deckIndex: string,
  fallback: number,
) => {
  try {
    // @ts-expect-error - deckIndex may not be valid key for deckList
    const otherSepSize = unitInfo.deckList[deckIndex].separationSize;
    return otherSepSize;
  } catch (error) {
    return fallback;
  }
};

export const tryGetDeck = (
  unitInfo: Extract<UnitInfoInterface, UnitInfoScreenBase<number, unknown>>,
  deckIndex: string,
) => {
  // @ts-expect-error - deckIndex may not be valid key for deckList
  return unitInfo.deckList[deckIndex] ?? {};
};

/**
 * Merges parts of otherUnit.unitInfo with unit.unitInfo
 * based on the type of unit.
 *
 * Usefull for e.g. replacing a unit with another
 */
export const mergeUnit = (unit: UnitInterface, otherUnit?: UnitInterface) => {
  if (!otherUnit) {
    return unit;
  }

  switch (unit.type) {
    case UnitTypes.GRIZZLY_SCREEN:
    case UnitTypes.GENERIC_SCREEN:
    case UnitTypes.CONVENTIONAL_SCREEN:
    case UnitTypes.LINEAR_MOTION_SCREEN:
    case UnitTypes.MULTISLOPE_SCREEN:
    case UnitTypes.BANANA_SCREEN:
    case UnitTypes.FREEFALL_SCREEN_SS:
    case UnitTypes.FREEFALL_SCREEN_SF:
    case UnitTypes.KWATANI_SCREEN: {
      /**
       * If the unit is a screen the nbrOfDecks, deck separaion size
       * and layout input/output ports are copied from "otherUnit"
       */
      const otherUnitInfo = otherUnit.unitInfo as Extract<
        UnitInfoInterface,
        UnitInfoScreenBase<number, unknown>
      >;

      const unitInfo = unit.unitInfo as Extract<
        UnitInfoInterface,
        UnitInfoScreenBase<number, unknown>
      >;

      let nbrOfDecks = Math.min(
        otherUnitInfo.nbrOfDecks,
        maxNbrOfDecks[unit.type],
      );

      nbrOfDecks = clamp(
        nbrOfDecks,
        minNbrOfDecks[unit.type],
        maxNbrOfDecks[unit.type],
      );

      const deckList = Object.entries(unitInfo.deckList).reduce(
        (acc, [key, deck]) => {
          return {
            ...acc,
            [key]: {
              ...tryGetDeck(unitInfo, key),
              separationSize: tryGetSepSize(
                otherUnitInfo,
                key,
                deck.separationSize,
              ),
            },
          };
        },
        {},
      );

      return {
        ...unit,
        unitInfo: {
          ...unit.unitInfo,
          nbrOfDecks,
          deckList,
        },
        layout: {
          ...unit.layout,
          inputPorts: 1,
          outputPorts: nbrOfDecks + 1,
        },
      };
    }
    default:
      return {
        ...unit,
        layout: {
          ...unit.layout,
          ...getPortsForUnit(unit.type),
        },
      };
  }
};
